import {
  FARM_DIAGNOSTICS_PRACTICE_FALLOW,
  FARM_DIAGNOSTICS_PRACTICE_PASTURE,
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TREND_DECLINING,
  FARM_DIAGNOSTICS_TREND_IMPROVING,
  FARM_DIAGNOSTICS_TREND_STABLE,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { convertToKg } from 'utils/unitConversion';
import { COST_CATEGORY_ORDER } from 'pages/FarmsPage/Workspace/Insights/costCategories';
import type {
  FarmDiagnosticsTimeRange,
  FarmDiagnosticsYearOverride,
  PracticeTag,
  SummaryEntityCategory,
} from '../../../../redux/slices/InsightsSlice';

/** Trend threshold — rolling-average % change at/above this is "improving", at/below its negation is "declining". */
const TREND_THRESHOLD_PCT = 5;

/**
 * Every cost category the Farm Diagnostics Overview table exposes, fixed
 * display order — shared by `useFarmDiagnosticsSeries` (to compute effective
 * $/ha) and `FarmDiagnosticsTable` (to render an editable field per
 * category), so a category a user overrides/what-ifs is never silently
 * dropped just because Epic 1 recorded $0 for it that year.
 */
export const FARM_DIAGNOSTICS_COST_CATEGORY_ORDER = COST_CATEGORY_ORDER;

/**
 * Converts a per-ha yield already expressed in `yieldUnit` (e.g. 't') to
 * kg/ha. Returns null when `yieldUnit` isn't a recognised mass unit (e.g.
 * bales) — callers must surface this as "can't compute", never assume kg.
 */
export const resolveYieldKgPerHa = (
  yieldValue: number,
  yieldUnit: string,
  gUnits: GUnit[]
): number | null => convertToKg(yieldValue, { unit: yieldUnit, globalId: null }, gUnits);

/** WUE Index = yield (kg/ha) ÷ rainfall (mm). Null when rainfall is missing/zero — rainfall is a required manual input, never defaulted. */
export const computeWueIndex = (
  yieldKgPerHa: number,
  rainfallMm: number | null
): number | null => (rainfallMm && rainfallMm > 0 ? yieldKgPerHa / rainfallMm : null);

/** Gross Margin ($/ha) = yield (per ha, in its own unit e.g. t/ha) × price ($/unit) − variable costs ($/ha). */
export const computeGrossMarginPerHa = (
  yieldValue: number,
  pricePerUnit: number,
  variableCostPerHa: number
): number => yieldValue * pricePerUnit - variableCostPerHa;

/** Financial WUE ($/mm) = Gross Margin ($/ha) ÷ rainfall (mm). Null when rainfall is missing/zero. */
export const computeFinancialWue = (
  gmPerHa: number,
  rainfallMm: number | null
): number | null => (rainfallMm && rainfallMm > 0 ? gmPerHa / rainfallMm : null);

export interface YearValue {
  year: number;
  value: number | null;
}

export interface RollingAveragePoint {
  year: number;
  rollingAvg: number | null;
}

/**
 * 3-year rolling average over STRICT CONSECUTIVE calendar years only — year
 * N needs a row for year N-1 and year N-2 to both exist in `series` with a
 * non-null value. A calendar gap (no row at all for N-1, e.g. the paddock
 * had no crop that year) resets the requirement rather than falling back to
 * "3 most recent years with data" — confirmed product decision, since
 * skipping over gaps would silently blend non-adjacent seasons together.
 */
export const computeRollingAverage3yr = (
  series: YearValue[]
): RollingAveragePoint[] => {
  const byYear = new Map(series.map((point) => [point.year, point.value]));

  return series.map(({ year }) => {
    const current = byYear.get(year);
    const prev1 = byYear.get(year - 1);
    const prev2 = byYear.get(year - 2);

    if (
      current == null ||
      !byYear.has(year - 1) ||
      prev1 == null ||
      !byYear.has(year - 2) ||
      prev2 == null
    ) {
      return { year, rollingAvg: null };
    }

    return { year, rollingAvg: (current + prev1 + prev2) / 3 };
  });
};

export type TrendDirection =
  | typeof FARM_DIAGNOSTICS_TREND_IMPROVING
  | typeof FARM_DIAGNOSTICS_TREND_STABLE
  | typeof FARM_DIAGNOSTICS_TREND_DECLINING;

/**
 * Classifies the change from rolling-average year N-1 to year N. Null when
 * either rolling average is unavailable (fewer than 3 consecutive years of
 * data on one side) — never guessed from a partial comparison.
 */
export const classifyTrend = (
  rollingAvgN: number | null,
  rollingAvgN1: number | null
): TrendDirection | null => {
  if (rollingAvgN == null || rollingAvgN1 == null || rollingAvgN1 === 0) {
    return null;
  }

  const pctChange = ((rollingAvgN - rollingAvgN1) / Math.abs(rollingAvgN1)) * 100;
  if (pctChange >= TREND_THRESHOLD_PCT) return FARM_DIAGNOSTICS_TREND_IMPROVING;
  if (pctChange <= -TREND_THRESHOLD_PCT) return FARM_DIAGNOSTICS_TREND_DECLINING;
  return FARM_DIAGNOSTICS_TREND_STABLE;
};

/** Fallow and Pasture are each mutually exclusive with the other and with every active-cropping practice — a paddock-year is either fallow, or pasture, or has zero-or-more active practices, never a mix. */
export const EXCLUSIVE_PRACTICE_TAGS: PracticeTag[] = [
  FARM_DIAGNOSTICS_PRACTICE_FALLOW,
  FARM_DIAGNOSTICS_PRACTICE_PASTURE,
];

/**
 * Resolves what a farm-year's practice-tag selection should become after
 * the user's raw multi-select change, enforcing that Fallow/Pasture are
 * mutually exclusive with each other and with every active-cropping
 * practice (No-till, Conventional till, Cover crop).
 *
 * `newSelection` is the Select's own onChange payload — the full new set,
 * not a delta — and this returns the corrected full set to actually store.
 * Only an *addition* re-triggers the exclusivity rule: if the newest tag
 * (present in newSelection but not previousSelection) is Fallow or
 * Pasture, the result collapses to just that one tag; if it's an active
 * practice, any Fallow/Pasture already selected is dropped. A pure
 * deselection (newSelection ⊆ previousSelection, nothing added) passes
 * through unchanged — removing a tag never re-applies the rule.
 */
export const resolvePracticeTagsAfterChange = (
  previousSelection: PracticeTag[],
  newSelection: PracticeTag[]
): PracticeTag[] => {
  const added = newSelection.filter((tag) => !previousSelection.includes(tag));
  if (added.length === 0) return newSelection;

  // Multiple simultaneous additions only happen via a single multi-select
  // change; the most recently listed addition decides which branch below
  // applies (the exclusive-tag branch always wins if any exclusive tag was
  // added, since it's a stronger constraint than "keep the active ones").
  const addedExclusive = added.find((tag) => EXCLUSIVE_PRACTICE_TAGS.includes(tag));
  if (addedExclusive) return [addedExclusive];

  return newSelection.filter((tag) => !EXCLUSIVE_PRACTICE_TAGS.includes(tag));
};

/**
 * Narrows a set of `{ year }` rows down to the window described by
 * `timeRange`. `last3`/`last5` are relative to the paddock's own latest
 * data year (not today's calendar year), so a paddock whose last plan is
 * two years old still shows its most recent 3/5 years rather than an
 * empty/near-empty window. Returns everything available when there are
 * fewer rows than the requested window — never an error or empty result.
 */
export const filterRowsByTimeRange = <T extends { year: number }>(
  rows: T[],
  timeRange: FarmDiagnosticsTimeRange
): T[] => {
  if (timeRange.mode === FARM_DIAGNOSTICS_TIME_RANGE_ALL) return rows;

  if (timeRange.mode === FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM) {
    const start = timeRange.customStartYear ?? -Infinity;
    const end = timeRange.customEndYear ?? Infinity;
    return rows.filter((row) => row.year >= start && row.year <= end);
  }

  // The N numerically highest DISTINCT years present, not a calendar-distance
  // cutoff (maxYear - (N-1)) — paddock data is sparse (crop years can skip
  // years), so a cutoff would silently return fewer than N data points even
  // when N+ years of data exist further back.
  const windowSize =
    timeRange.mode === FARM_DIAGNOSTICS_TIME_RANGE_LAST_3 ? 3 : 5;
  const distinctYearsDesc = Array.from(new Set(rows.map((row) => row.year))).sort(
    (a, b) => b - a
  );
  const keepYears = new Set(distinctYearsDesc.slice(0, windowSize));
  return rows.filter((row) => keepYears.has(row.year));
};

/**
 * The year range shown in a "Farm-Level Inputs" panel.
 *
 * Rainfall and management practice are FARM-level concepts (one value per
 * year applies to every paddock under that farm) — so "which years are
 * relevant" must be evaluated once across the farm's combined history, not
 * independently per paddock. This takes the union of every paddock's FULL
 * (unfiltered) history years, applies the SAME last3/last5/custom/all
 * windowing `filterRowsByTimeRange` uses (the farm's own 3/5 most recent
 * active years, not each paddock's own independent 3/5), then contiguous-
 * fills between that windowed result's min/max so a true gap year within
 * the final window (e.g. Fallow, which has no Epic 1 row) can still be
 * tagged.
 *
 * An earlier version filtered each paddock independently before taking the
 * union, which stayed wrong for the same reason a global min/max fill was:
 * two paddocks with staggered histories each contribute their OWN 3-year
 * window, and the union of two non-overlapping 3-year windows is not 3
 * years. Windowing the combined farm-level year set once, up front, is the
 * only version of this that actually shows ~3 years for "Last 3 years".
 */
export const resolveFarmYearInputYears = (
  paddockAllRows: { year: number }[][],
  timeRange: FarmDiagnosticsTimeRange
): number[] => {
  const allFarmYears = Array.from(
    new Set(paddockAllRows.flatMap((rows) => rows.map((r) => r.year)))
  ).map((year) => ({ year }));

  const windowed = filterRowsByTimeRange(allFarmYears, timeRange);
  if (windowed.length === 0) return [];

  const min = Math.min(...windowed.map((r) => r.year));
  const max = Math.max(...windowed.map((r) => r.year));
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
};

export interface TrendFactor {
  label: string;
  /** % change from the previous year to this one. Null if either year's underlying value is unavailable. */
  changePct: number | null;
}

const percentChange = (
  previous: number | null,
  current: number | null
): number | null =>
  previous != null && current != null && previous !== 0
    ? ((current - previous) / Math.abs(previous)) * 100
    : null;

/**
 * Biological (WUE) trend factors — the two direct inputs to
 * WUE Index = yield ÷ rainfall. Structural types (not `FarmDiagnosticsYearSeries`
 * itself) to avoid a circular import — that file already imports from here.
 * Deliberately just "what changed since last year", not a claim to explain
 * the full 3-yr rolling-average shift (see FARM_DIAGNOSTICS_TREND_FACTORS_CAVEAT).
 */
export const computeWueTrendFactors = (
  current: { yieldKgPerHa: number | null; rainfallMm: number | null } | undefined,
  previous: { yieldKgPerHa: number | null; rainfallMm: number | null } | undefined
): TrendFactor[] => {
  if (!current || !previous) return [];
  return [
    { label: 'Yield', changePct: percentChange(previous.yieldKgPerHa, current.yieldKgPerHa) },
    { label: 'Rainfall', changePct: percentChange(previous.rainfallMm, current.rainfallMm) },
  ];
};

export interface KeyMetricsSummary {
  latestRollingAvg: number | null;
  trendPct: number | null;
  trend: TrendDirection | null;
  yoyChange: number | null;
}

/**
 * Shared shape behind every "Key Metrics" tile (latest 3-yr rolling avg,
 * trend %, year-on-year change) — used identically by WUE Trend, Financial
 * Efficiency, and both halves of Bio vs Financial, so their summary tiles
 * are structurally consistent (same fields, same derivation), not just
 * visually similar.
 */
export const computeKeyMetricsSummary = (
  years: { year: number; value: number | null }[],
  rollingAvg: RollingAveragePoint[],
  trendByYear: Record<number, TrendDirection | null>
): KeyMetricsSummary => {
  const latest = years[years.length - 1];
  const previous = years[years.length - 2];
  const latestRollingAvg =
    rollingAvg.find((p) => p.year === latest?.year)?.rollingAvg ?? null;
  const prevRollingAvg = latest
    ? rollingAvg.find((p) => p.year === latest.year - 1)?.rollingAvg ?? null
    : null;
  const trendPct = percentChange(prevRollingAvg, latestRollingAvg);
  const latestValue = latest?.value ?? null;
  const previousValue = previous?.value ?? null;
  const yoyChange =
    latestValue != null && previousValue != null ? latestValue - previousValue : null;
  const trend = latest ? (trendByYear[latest.year] ?? null) : null;
  return { latestRollingAvg, trendPct, trend, yoyChange };
};

/**
 * Financial WUE trend factors — the inputs to
 * Financial WUE = (yield × price − variable cost/ha) ÷ rainfall.
 */
export const computeFinancialTrendFactors = (
  current:
    | {
        pricePerUnit: number;
        yieldValue: number;
        variableCostPerHa: number;
        rainfallMm: number | null;
      }
    | undefined,
  previous:
    | {
        pricePerUnit: number;
        yieldValue: number;
        variableCostPerHa: number;
        rainfallMm: number | null;
      }
    | undefined
): TrendFactor[] => {
  if (!current || !previous) return [];
  return [
    { label: 'Price', changePct: percentChange(previous.pricePerUnit, current.pricePerUnit) },
    { label: 'Yield', changePct: percentChange(previous.yieldValue, current.yieldValue) },
    {
      label: 'Variable cost/ha',
      changePct: percentChange(previous.variableCostPerHa, current.variableCostPerHa),
    },
    { label: 'Rainfall', changePct: percentChange(previous.rainfallMm, current.rainfallMm) },
  ];
};

/**
 * One Yearly Overview row as its Total row reads it. Structural (not
 * `WueTrendYearRow`) so this file needn't import computeWueTrend.
 */
export interface YearlyOverviewTotalsRow {
  result: {
    areaHa: number;
    costsTotal: Partial<Record<SummaryEntityCategory, number>>;
  } | null;
  yearOverride?: FarmDiagnosticsYearOverride;
  production: number | null;
  /** $/ha for the row. */
  income: number | null;
  /** Effective (post-override) $ total for the row. */
  totalCost: number | null;
}

export interface YearlyOverviewTotals {
  /** Effective (post-override) $ per cost category, summed across rows. */
  byCategory: Partial<Record<SummaryEntityCategory, number>>;
  areaHa: number;
  production: number;
  /** Area-weighted average $/ha (income is $/ha per row, so it isn't summed). */
  incomeAvgPerHa: number;
  totalCost: number;
}

/**
 * The Yearly Overview's Total row. Rows with no Plan/Actual data are
 * skipped. Cost categories use the same effective (post-override) figure as
 * each row's cell.
 */
export const computeYearlyOverviewTotals = (
  rows: YearlyOverviewTotalsRow[]
): YearlyOverviewTotals => {
  const byCategory: Partial<Record<SummaryEntityCategory, number>> = {};
  let areaHa = 0;
  let production = 0;
  let incomeDollars = 0;
  let incomeAreaHa = 0;
  let totalCost = 0;
  for (const row of rows) {
    const { result, yearOverride } = row;
    if (!result) continue;
    areaHa += result.areaHa;
    production += row.production ?? 0;
    if (row.income != null) {
      incomeDollars += row.income * result.areaHa;
      incomeAreaHa += result.areaHa;
    }
    totalCost += row.totalCost ?? 0;
    for (const category of FARM_DIAGNOSTICS_COST_CATEGORY_ORDER) {
      byCategory[category] =
        (byCategory[category] ?? 0) +
        (yearOverride?.costOverrides?.[category] ??
          result.costsTotal[category] ??
          0);
    }
  }
  return {
    byCategory,
    areaHa,
    production,
    incomeAvgPerHa: incomeAreaHa > 0 ? incomeDollars / incomeAreaHa : 0,
    totalCost,
  };
};

/**
 * A $ total as the Total row shows it — matching the column header: the
 * total itself in Total ($) mode, the area-weighted average $/ha
 * (Σ$ ÷ Σha) in $/ha mode.
 */
export const toYearlyOverviewFooterCost = (
  dollars: number,
  totalAreaHa: number,
  perHa: boolean
): number => (perHa ? (totalAreaHa > 0 ? dollars / totalAreaHa : 0) : dollars);
